import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  addMembersApi,
  addPlayerAccountsApi,
  addPlayerAccountCSVApi,
  addPlayerAccountsCSVApi,
  changeAllMemberSaigeAccessApi,
  changeMemberAdminRoleApi,
  changeMemberSaigeAccessApi,
  getBusinessSettingsData,
  getQuotaUpdate,
  removeMemberApi,
  resendPlayerAccountInviteApi,
} from '../api/businessSettingsApiService'
import {
  parseAddMemberVmToAddMemberData,
  parseAdminSettingsDataToVm,
  parseBussinessPlayerAccountData,
} from '../api/typeConverters'
import {
  BusinessPlayerAccountParams,
  BusinessQuotaInfo,
  BussinessPlayerAccounts,
  TAddMember,
  Tag,
  TBusinessSettings,
  TMember,
} from '../types/commonTypes'
import { useConfirmation } from './ConfirmationContext'
import { useNotification } from './NotificationContext'
import { useUser } from './userContext'
import { AxiosError } from 'axios'
import { searchPlayerAccounts } from '../api/userApiService'
import { noop } from '../util/functional'
import i18n from '../i18n'
import { Player } from '../pages/settings/organization/components/AddPlayersModal'

export type PaginationSelectOption = {
  value: number
  label: string
}

export type SettingsFilters = {
  tags: Tag[]
}

export type csvFileInfo = { fileName: string; inputFile: Blob }

type BusinessSettingsContextValue = {
  isFetchingData: boolean
  data: TBusinessSettings | undefined
  quota: BusinessQuotaInfo | undefined
  businessPlayerAccounts: BussinessPlayerAccounts | undefined
  addMembers: (members: TAddMember[], message?: string) => Promise<boolean>
  changeMemberRole: (userName: string) => Promise<boolean>
  toggleMemberSaigeAccess: (userName: string) => Promise<boolean>
  setAllMembersSaigeAccess: (enabledState: boolean) => Promise<boolean>
  removeMember: (userName: string) => Promise<boolean>
  updateQuota: () => Promise<boolean>
  getBusinessPlayerAccountInfo: (data?: BusinessPlayerAccountParams) => Promise<boolean>
  currentPage: number
  pageSize: number
  lastPage: number
  allPageOptions: PaginationSelectOption[]
  selectPageSize: (newPageSize: number) => void
  prev: () => void
  next: () => void
  setPage: (newPage: number) => void
  sortBy?: SettingsSortBy
  sortDir?: SettingsSortDir
  setSort?: (sortBy: SettingsSortBy, sortDir: SettingsSortDir) => void
  filters?: SettingsFilters
  checkPlayerAccountsCSV: (file: csvFileInfo) => Promise<any>
  addPlayerAccountsCSV: (info: any) => Promise<boolean>
  resendPlayerAccountInvite: (playerEmai: string) => Promise<boolean>
  addPlayerAccounts: (players: Player[]) => Promise<boolean>
}

export enum SettingsSortBy {
  NAME = 'user_name',
  TYPE = 'type',
  EMAIL = 'email',
  TAGS = 'tags',
}

export enum SettingsSortDir {
  ASC = ' asc',
  DESC = ' desc',
}

const INITIAL_VALUES: BusinessSettingsContextValue = {
  isFetchingData: true,
  data: undefined,
  quota: undefined,
  businessPlayerAccounts: undefined,
  addMembers: () => Promise.resolve(false),
  removeMember: () => Promise.resolve(false),
  changeMemberRole: () => Promise.resolve(false),
  toggleMemberSaigeAccess: () => Promise.resolve(false),
  setAllMembersSaigeAccess: () => Promise.resolve(false),
  updateQuota: () => Promise.resolve(false),
  getBusinessPlayerAccountInfo: () => Promise.resolve(false),
  currentPage: 1,
  pageSize: 30,
  lastPage: 1,
  allPageOptions: [] as PaginationSelectOption[],
  selectPageSize: noop,
  prev: noop,
  next: noop,
  setPage: noop,
  sortBy: SettingsSortBy.NAME,
  sortDir: SettingsSortDir.ASC,
  setSort: noop,
  checkPlayerAccountsCSV: () => Promise.resolve(false),
  addPlayerAccountsCSV: () => Promise.resolve(false),
  resendPlayerAccountInvite: () => Promise.resolve(false),
  addPlayerAccounts: () => Promise.resolve(false),
}

const BusinessSettingsContext = createContext<BusinessSettingsContextValue>(INITIAL_VALUES)

export const BusinessSettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isFetchingData, setIsFetchingData] = useState<boolean>(true)
  const [data, setData] = useState<TBusinessSettings>()
  const [quota, setQuota] = useState<BusinessQuotaInfo>()
  const [businessPlayerAccounts, setBusinessPlayerAccounts] = useState<BussinessPlayerAccounts>()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(30)
  const [lastPage, setLastpage] = useState(1)
  const [allPageOptions, setAllPageOptions] = useState<PaginationSelectOption[]>([{ value: 1, label: '1' }])
  const [sortBy, setSortBy] = useState<SettingsSortBy>(SettingsSortBy.NAME)
  const [sortDir, setSortDir] = useState<SettingsSortDir>(SettingsSortDir.ASC)
  const [refreshCounter, setRefreshCounter] = useState<number>(0)
  const { notifyError, notifySuccess } = useNotification()
  const { requestConfirmation } = useConfirmation()
  const { t } = useTranslation()
  const { user } = useUser()

  const next = useCallback(() => {
    if (isFetchingData) {
      return
    }

    setCurrentPage((currentState) => currentState + 1)
  }, [isFetchingData])

  const prev = useCallback(() => {
    if (isFetchingData) {
      return
    }

    setCurrentPage((currentState) => currentState - 1)
  }, [isFetchingData])

  const selectPageSize = useCallback(
    (newPageSize: number) => {
      if (isFetchingData) {
        return
      }

      setCurrentPage(1)
      setPageSize(newPageSize)
    },
    [isFetchingData],
  )

  const setPage = useCallback(
    (newPage: number) => {
      if (isFetchingData) {
        return
      }

      setCurrentPage(newPage)
    },
    [isFetchingData],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    const calculateAllPageOptions = Array.from({ length: lastPage }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}`,
    }))
    setAllPageOptions(calculateAllPageOptions)
  }, [lastPage])

  const setSort = useCallback((sortBy: SettingsSortBy, sortDir: SettingsSortDir) => {
    setSortBy(sortBy)
    setSortDir(sortDir)
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    if (user == null) {
      return
    }
    setIsFetchingData(true)
    getBusinessSettingsData({ businessId: user.activeBusiness.id })
      .then((response) => {
        if (response.success) {
          setData(parseAdminSettingsDataToVm(response.value))
        } else {
          console.error(response.error)
          notifyError({
            title: t('business_settings.fetch_data_error_notification.title', 'Failed to get data'),
            content: t(
              'business_settings.fetch_data_error_notification.content',
              'An error occurred while getting data. Please try again or contact us for support.',
            ),
          })
        }
      })
      .finally(() => {
        setIsFetchingData(false)
      })
  }, [notifyError, t, user, refreshCounter])

  const getBusinessPlayerAccountInfo = useCallback(
    async (data: BusinessPlayerAccountParams | undefined) => {
      if (user == null) {
        return false
      }
      const { search, tags, showAll, page } = data || {}
      setIsFetchingData(true)
      const tagIds: string[] = tags
        ? tags?.map((tag) => {
            return tag.id.toString()
          })
        : []
      searchPlayerAccounts({ search: search ?? '', tagIds: tagIds, showAll: showAll || false, page: page ?? 1 }).then(
        (playerAccountResponse): any => {
          if (playerAccountResponse.success) {
            getBusinessSettingsData({ businessId: user.activeBusiness.id }).then((response) => {
              if (response.success) {
                setBusinessPlayerAccounts(parseBussinessPlayerAccountData(response.value, playerAccountResponse.value))
                setCurrentPage(playerAccountResponse.value.current_page)
                setLastpage(playerAccountResponse.value.total_pages)
              }
              setIsFetchingData(false)
              return response.success
            })
          }
          setIsFetchingData(false)
          return playerAccountResponse.success
        },
      )
      setIsFetchingData(false)
      return false
    },
    [user],
  )

  const updateQuota = useCallback(async () => {
    try {
      if (user?.activeBusiness.id == null) {
        throw new Error('Cannot perform action without active business id')
      }
      const response = await getQuotaUpdate({ businessId: user.activeBusiness.id })
      if (response.success) {
        setQuota({
          totalQuotaUsage: response.value.total_quota_usage || null,
          totalSeats: response.value.total_seats || null,
          usedSeats: response.value.used_seats || null,
          freeSeats:
            response.value.total_seats && response.value.used_seats
              ? response.value.total_seats - response.value.used_seats
              : null,
        })
        return true
      } else {
        throw response.error
      }
    } catch (error) {
      console.error(error)
      notifyError({
        title: t('business_settings.update_member_role_error_notification.title', 'Failed to update member role'),
        content: t(
          'business_settings.update_member_role_error_notification.content',
          'An error occurred while updating member role. Please try again or contact us for support.',
        ),
      })
      return false
    }
  }, [notifyError, t, user])

  const changeMemberRole = useCallback(
    async (userName: string) => {
      try {
        if (user?.activeBusiness.id == null) {
          throw new Error('Cannot perform action without active business id')
        }
        const response = await changeMemberAdminRoleApi({ businessId: user.activeBusiness.id, userName })
        if (response.success) {
          setRefreshCounter((prev) => prev + 1)
          return true
        } else {
          throw response.error
        }
      } catch (error) {
        console.error(error)
        notifyError({
          title: t('business_settings.update_member_role_error_notification.title', 'Failed to update member role'),
          content: t(
            'business_settings.update_member_role_error_notification.content',
            'An error occurred while updating member role. Please try again or contact us for support.',
          ),
        })
        return false
      }
    },
    [notifyError, t, user],
  )

  const toggleMemberSaigeAccess = useCallback(
    async (userName: string) => {
      try {
        if (user?.activeBusiness.id == null) {
          throw new Error('Cannot perform action without active business id')
        }
        const response = await changeMemberSaigeAccessApi({ businessId: user.activeBusiness.id, userName })
        //Do a direct toggle of the one member's saigeAccess instead of reloading of full data
        //Full reload causes delays in being able to toggle many members
        if (response.success) {
          const temp = JSON.parse(JSON.stringify(data?.members)) as TMember[]
          const ind = temp.findIndex((m) => m.email === userName)
          if (ind > -1) {
            temp[ind].saigeAccess = !temp[ind].saigeAccess
            setData((prev) => {
              if (prev) {
                return {
                  ...prev,
                  members: temp,
                }
              }
            })
          }
          return true
        } else {
          throw response.error
        }
      } catch (error) {
        console.error(error)
        notifyError({
          title: t(
            'business_settings.update_member_saige_access_error_notification.title',
            "Failed to update member's Saige access",
          ),
          content: t(
            'business_settings.update_member_role_error_notification.content',
            'An error occurred while updating member role. Please try again or contact us for support.',
          ),
        })
        return false
      }
    },
    [notifyError, t, user, data],
  )

  const setAllMembersSaigeAccess = useCallback(
    async (enabledState: boolean) => {
      try {
        if (user?.activeBusiness.id == null) {
          throw new Error('Cannot perform action without active business id')
        }
        const response = await changeAllMemberSaigeAccessApi({ businessId: user.activeBusiness.id, enabledState })
        if (response.success) {
          setRefreshCounter((prev) => prev + 1)
          return true
        } else {
          throw response.error
        }
      } catch (error) {
        console.error(error)
        notifyError({
          title: t(
            'business_settings.update_member_saige_access_error_notification.title',
            "Failed to update member's Saige access",
          ),
          content: t(
            'business_settings.update_member_role_error_notification.content',
            'An error occurred while updating member role. Please try again or contact us for support.',
          ),
        })
        return false
      }
    },
    [notifyError, t, user],
  )

  const removeMember = useCallback(
    async (userName: string) => {
      const confirm = await requestConfirmation({
        title: t('business_settings.remove_member_confirmation_notification.title', 'Confirm remove action'),
        text: (
          <Trans
            i18nKey='business_settings.remove_member_confirmation_notification.text'
            values={{ user_name: userName }}
            components={{ 1: <b /> }}
          >
            {
              'Are you sure you want to remove member <1>%{user_name}</1>? They will not be able to access your organisation.'
            }
          </Trans>
        ),
        confirmActionText: t('business_settings.remove_member_confirmation_notification.confirm_button_text', 'Remove'),
      })
      if (!confirm) {
        return false
      }
      try {
        if (user?.activeBusiness.id == null) {
          throw new Error('Cannot perform action without active business id')
        }
        const response = await removeMemberApi({ businessId: user.activeBusiness.id, userName })
        if (response.success) {
          setRefreshCounter((prev) => prev + 1)
          return true
        } else {
          throw response.error
        }
      } catch (error) {
        console.error(error)
        notifyError({
          title: t('business_settings.remove_member_error_notification.title', 'Failed to remove member'),
          content: t(
            'business_settings.remove_member_error_notification.content',
            'An error occurred while removing member. Please try again or contact us for support.',
          ),
        })
        return false
      }
    },
    [notifyError, t, requestConfirmation, user],
  )

  const addMembers = useCallback(
    async (members: TAddMember[], message?: string) => {
      try {
        if (user?.activeBusiness.id == null) {
          throw new Error('Cannot perform action without active business id')
        }
        const response = await addMembersApi({
          businessId: user.activeBusiness.id,
          members: members.map(parseAddMemberVmToAddMemberData),
          message: message,
        })
        if (response.success) {
          setRefreshCounter((prev) => prev + 1)
          notifySuccess({
            title: t('business_settings.add_members_success_notification.title', {
              count: members.length,
              defaultValue: 'Added members',
            }),
            content: t('business_settings.add_members_success_notification.content', {
              count: members.length,
              defaultValue: 'New members were added to your organisation',
            }),
          })
          return true
        } else {
          throw response.error
        }
      } catch (error) {
        const err = error as AxiosError
        const errorMessage = err.response?.data
        if (typeof errorMessage === 'string') {
          notifyError({
            title: t('business_settings.add_members_error_notification.title', 'Failed to add members'),
            content: t('business_settings.add_members_error_notification_with_message.content', {
              defaultValue:
                'An error occurred while adding members. Please try again or contact us for support. Error message: %{errorMessage}',
              errorMessage: errorMessage,
            }),
          })
        } else {
          notifyError({
            title: t('business_settings.add_members_error_notification.title', 'Failed to add members'),
            content: t(
              'business_settings.add_members_error_notification.content',
              'An error occurred while adding members. Please try again or contact us for support.',
            ),
          })
        }
        return false
      }
    },
    [notifySuccess, notifyError, t, user],
  )

  const checkPlayerAccountsCSV = useCallback(
    async ({ fileName, inputFile }: { fileName: string; inputFile: Blob }) => {
      const response = await addPlayerAccountCSVApi({ fileName: fileName, inputFile: inputFile })
      if (response.success) {
        notifySuccess({
          title: t('business_settings.add_player_csv_success_notification.title', 'CSV valid!'),
          content: t(
            'business_settings.add_player_csv_success_notification.content',
            'Invitations to player accounts can be send',
          ),
        })
      } else {
        notifyError({
          title: t('business_settings.add_player_csv_error_notification.title', 'CSV is not valid'),
          content: response.error.message,
        })
      }
      return response
    },
    [notifyError, notifySuccess, t],
  )

  const addPlayerAccountsCSV = useCallback(
    async (info: any) => {
      const response = await addPlayerAccountsCSVApi({ info: info })
      if (response.success) {
        notifySuccess({
          title: t('business_settings.add_players_from_csv_success_notification.title', 'Invitations send!'),
          content: t(
            'business_settings.add_players_from_csv_success_notification.content',
            'Invitations to join the player accounts have been send',
          ),
        })
      } else {
        notifyError({
          title: t('business_settings.add_players_from_csv_error_notification.title', 'Invitations not send!'),
          content: response.error.message,
        })
      }
      return response.success
    },
    [notifyError, notifySuccess, t],
  )

  const addPlayerAccounts = useCallback(
    async (players: Player[]) => {
      var success = true
      if (user) {
        for (const player of players) {
          const response = await addPlayerAccountsApi({ player: player, invitedBy: user?.id, language: i18n.language })
          if (!response.success) success = false
        }
        if (success) {
          notifySuccess({
            title: t('business_settings.add_players_success_notification.title', 'Invitations send!'),
            content: t(
              'business_settings.add_players_success_notification.content',
              'Invitations to join the player accounts have been send',
            ),
          })
        } else {
          notifyError({
            title: t('business_settings.add_players_error_notification.title', 'Invitations not send!'),
            content: t(
              'business_settings.add_players_error_notification.content',
              'Try again later or contact support',
            ),
          })
        }
        return success
      }
      notifyError({
        title: t('business_settings.add_players_error_notification.title', 'Invitations not send!'),
        content: t('business_settings.add_players_error_notification.content', 'Try again later or contact support'),
      })
      return false
    },
    [notifyError, notifySuccess, t, user],
  )

  const resendPlayerAccountInvite = useCallback(
    async (playerEmail: string) => {
      const response = await resendPlayerAccountInviteApi({ playerEmail })
      if (response.success) {
        notifySuccess({
          title: t('business_settings.resend_invite_success_notification.title', 'Invite renewed'),
          content: t('business_settings.resend_invite_success_notification.content', {
            email: playerEmail,
            defaultValue: 'New invitation send to %{email}',
          }),
        })
      } else {
        notifyError({
          title: t('business_settings.resend_invite_success_error.title', 'Sending invite failed'),
          content: t('business_settings.resend_invite_success_error.content', {
            email: playerEmail,
            defaultValue: 'We were unable to send invitation to %{email}. Please try again later of contact support.',
          }),
        })
      }
      return response.success
    },
    [notifyError, notifySuccess, t],
  )

  return (
    <BusinessSettingsContext.Provider
      value={{
        addMembers,
        removeMember,
        isFetchingData,
        changeMemberRole,
        data,
        updateQuota,
        quota,
        toggleMemberSaigeAccess,
        setAllMembersSaigeAccess,
        businessPlayerAccounts,
        getBusinessPlayerAccountInfo,
        currentPage,
        pageSize,
        lastPage,
        allPageOptions,
        sortBy,
        sortDir,
        setPage,
        selectPageSize,
        next,
        prev,
        setSort,
        checkPlayerAccountsCSV,
        addPlayerAccountsCSV,
        resendPlayerAccountInvite,
        addPlayerAccounts,
      }}
    >
      {children}
    </BusinessSettingsContext.Provider>
  )
}

export const useBusinessSettings = () => {
  const context = useContext(BusinessSettingsContext)
  if (!context) throw new Error('Expected to be wrapped in a BusinessSettingsContextProvider!')
  return context
}
